// ═══════════════════════════════════════════════════════════════
// BENEFIT TEMPLATE ROW
// Path: src/components/shows/BenefitTemplateRow.jsx
//
// Single row in the Benefit Templates list.
// Drag handle · Name · Category badge · Description · Status · Actions
//
// Props:
//   template       — benefit_templates row
//   onEdit         — (template) => void
//   onDeactivate   — (templateId) => void
//   onDelete       — (template) => void — triggers guard modal in parent
//   canEdit        — boolean (Admin/Manager)
//   canDelete      — boolean (Admin only)
//   dragHandleProps — props from drag-and-drop (or null if disabled)
//
// Blueprint: CMM_Show_Sponsorship_Blueprint_v1_1.md — Phase 3
// ═══════════════════════════════════════════════════════════════

import React from "react";
import {
  GripVertical,
  Edit3,
  EyeOff,
  Eye,
  Trash2,
  MoreVertical,
} from "lucide-react";
import { useApp } from "../../context/AppContext";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";

export const BenefitTemplateRow = ({
  template,
  onEdit,
  onDeactivate,
  onReactivate,
  onDelete,
  canEdit,
  canDelete,
  dragHandleProps,
}) => {
  const { palette } = useApp();
  const [showMenu, setShowMenu] = React.useState(false);

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "10px 12px",
        borderRadius: 8,
        border: `1px solid ${palette.cardBorder}`,
        background: template.is_active ? palette.cardBg : `${palette.cardBg}80`,
        opacity: template.is_active ? 1 : 0.7,
        transition: "all 0.2s",
        marginBottom: 6,
      }}
    >
      {/* Drag handle */}
      {canEdit && dragHandleProps && (
        <div
          {...dragHandleProps}
          style={{
            cursor: "grab",
            color: palette.text3,
            display: "flex",
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          <GripVertical size={ICON_SIZES.sm} />
        </div>
      )}

      {/* Name + Description */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <span
            style={{
              fontSize: FONT_SIZES.sm,
              fontWeight: 600,
              color: palette.text1,
              fontFamily: FONT_FAMILY,
            }}
          >
            {template.name}
          </span>

          {/* Category badge */}
          {template.category && (
            <span
              style={{
                fontSize: "0.65rem",
                fontWeight: 600,
                color: palette.accent1,
                background: `${palette.accent1}15`,
                padding: "2px 8px",
                borderRadius: 10,
                fontFamily: FONT_FAMILY,
                whiteSpace: "nowrap",
              }}
            >
              {template.category}
            </span>
          )}

          {/* Inactive badge */}
          {!template.is_active && (
            <span
              style={{
                fontSize: "0.65rem",
                fontWeight: 600,
                color: "#f59e0b",
                background: "#fef3c7",
                padding: "2px 8px",
                borderRadius: 10,
                fontFamily: FONT_FAMILY,
              }}
            >
              Inactive
            </span>
          )}
        </div>

        {/* Description (truncated) */}
        {template.description && (
          <p
            style={{
              margin: "2px 0 0",
              fontSize: FONT_SIZES.xs,
              color: palette.text3,
              fontFamily: FONT_FAMILY,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: 400,
            }}
          >
            {template.description}
          </p>
        )}
      </div>

      {/* Action menu */}
      {canEdit && (
        <div style={{ position: "relative", flexShrink: 0 }}>
          <button
            onClick={() => setShowMenu(!showMenu)}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: palette.text3,
              padding: 4,
              borderRadius: 4,
            }}
          >
            <MoreVertical size={ICON_SIZES.sm} />
          </button>

          {showMenu && (
            <>
              {/* Backdrop to close menu */}
              <div
                style={{ position: "fixed", inset: 0, zIndex: 99 }}
                onClick={() => setShowMenu(false)}
              />
              <div
                style={{
                  position: "absolute",
                  right: 0,
                  top: "100%",
                  background: palette.cardBg,
                  border: `1px solid ${palette.cardBorder}`,
                  borderRadius: 8,
                  boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                  zIndex: 100,
                  minWidth: 160,
                  overflow: "hidden",
                }}
              >
                {/* Edit */}
                <MenuButton
                  icon={Edit3}
                  label="Edit"
                  palette={palette}
                  onClick={() => {
                    setShowMenu(false);
                    onEdit(template);
                  }}
                />

                {/* Deactivate / Reactivate */}
                {template.is_active ? (
                  <MenuButton
                    icon={EyeOff}
                    label="Deactivate"
                    palette={palette}
                    onClick={() => {
                      setShowMenu(false);
                      onDeactivate(template.id);
                    }}
                  />
                ) : (
                  <MenuButton
                    icon={Eye}
                    label="Reactivate"
                    palette={palette}
                    onClick={() => {
                      setShowMenu(false);
                      onReactivate(template.id);
                    }}
                  />
                )}

                {/* Delete (Admin only) */}
                {canDelete && (
                  <MenuButton
                    icon={Trash2}
                    label="Delete"
                    palette={palette}
                    danger
                    onClick={() => {
                      setShowMenu(false);
                      onDelete(template);
                    }}
                  />
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ── Small helper for menu items ───────────────────────────────
const MenuButton = ({ icon: Icon, label, palette, danger, onClick }) => (
  <button
    onClick={onClick}
    style={{
      display: "flex",
      alignItems: "center",
      gap: 8,
      width: "100%",
      padding: "10px 14px",
      background: "none",
      border: "none",
      cursor: "pointer",
      fontSize: FONT_SIZES.sm,
      fontFamily: FONT_FAMILY,
      color: danger ? "#dc2626" : palette.text1,
      textAlign: "left",
    }}
    onMouseEnter={(e) =>
      (e.currentTarget.style.background = danger ? "#fef2f2" : `${palette.accent1}10`)
    }
    onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
  >
    <Icon size={ICON_SIZES.sm} />
    {label}
  </button>
);
